import React, { useEffect, useState } from 'react';
import { CITIZEN_FORM, CRAFT } from './CitizenPortrait';
import { HatGlyph } from './HatIcon';
import PanelDamage, { VB_H as FAULT_H } from './PanelDamage';
import SurveyCrew from './SurveyCrew';
import useRepairCycle, { CRITICAL, TIMING } from '../hooks/useRepairCycle';

/*
 * A repair crew posted to one section of the page.
 *
 * The panel degrades on its own clock -- cracks from the border inward, then
 * deeper, then chips off the edge, then it gives way. At that last stage the
 * damage clock stops and a crew is called. The crew flies in, a worker is
 * lowered to the fault, and the repair walks the panel back one stage at a
 * time until it is whole. Only then does the worker come up and the ship leave,
 * and only then does the damage clock start again somewhere else.
 *
 * The phases are state, not keyframes: a CSS animation cannot hold at a stage
 * for an unknown length of time, and the repair takes as long as the damage it
 * found. Durations come from TIMING so the transitions and the timers cannot
 * disagree about how long a ship takes to arrive.
 *
 * Posts are only offered to sections with a surface. A welder needs plating;
 * against bare canvas the sparks land on starfield, which is why the orrery
 * has no crew.
 *
 * Strictly decorative: aria-hidden, pointer-transparent, hidden until the
 * viewport is wide enough to have margins to work in.
 */

export const CREW_POSTS = {
  timeline: { night: 'devops', day: 'arch', side: 'left' },
  matrix: { night: 'dev', day: 'ux', side: 'right' },
};

const VB_W = 96;
const VB_H = 200;

/*
 * The crew is offset so the torch lands on the middle of the fault rather than
 * below it. The fault is anchored at the spot; the worker sits WORK_Y down its
 * own box, so the box has to start that far above the fault's centre line.
 */
const ALIGN = FAULT_H / 2 - 150;
const CRAFT_Y = 30;
const DOCK_Y = 48; // where the worker rides when stowed
const WORK_Y = 150; // and where it hangs to work

/*
 * Sparks each carry a vector and a delay rather than sharing one scale, so they
 * throw along different lines -- a uniform burst reads as a pulsing dot.
 */
const SPARKS = [
  { len: 9, dx: 13, dy: -9, delay: 0 },
  { len: 6, dx: 15, dy: -2, delay: 0.09 },
  { len: 11, dx: 11, dy: 7, delay: 0.17 },
  { len: 5, dx: 9, dy: 12, delay: 0.26 },
  { len: 8, dx: 16, dy: 4, delay: 0.34 },
  { len: 4, dx: 6, dy: -13, delay: 0.43 },
];

/* Somewhere in the middle three-quarters, so damage never lands on a corner. */
const pickSpot = () => 12 + Math.random() * 62;

/* Crews only run where there is margin to run in; no timers below that. */
const hasRoom = () =>
  typeof window === 'undefined' || window.matchMedia?.('(min-width: 1400px)')?.matches !== false;

const calm = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;

const Unit = ({ id, side, sky, crew }) => {
  const craft = CRAFT[id];
  const form = CITIZEN_FORM[id];

  // Everything leans toward the column: left crews work right, right crews left.
  const inward = side === 'left' ? 1 : -1;
  const craftX = side === 'left' ? 20 : VB_W - 20;
  const workX = side === 'left' ? 64 : VB_W - 64;

  return (
    <div
      className={`crew-unit is-${side} is-${sky} crew-${crew}`}
      style={{
        '--cz-ink': form.ink,
        '--accent': `var(--tmc-hat-${id})`,
        // How far the worker is hauled back up, derived from the geometry.
        '--dock-x': `${craftX - workX}px`,
        '--dock-y': `${DOCK_Y - WORK_Y}px`,
        // Off-screen, on the outward side, which is where it flies in from.
        '--fly': `${inward * -210}px`,
        '--t-inbound': `${TIMING.inbound}ms`,
        '--t-deploy': `${TIMING.deploy}ms`,
        '--t-recover': `${TIMING.recover}ms`,
        '--t-outbound': `${TIMING.outbound}ms`,
      }}
    >
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} focusable="false" aria-hidden="true">
        <path className="crew-tether" d={`M ${craftX} ${CRAFT_Y + 12} L ${workX} ${WORK_Y - 28}`} />

        {/*
          * The SVG transform places the craft; the CSS animation on the inner
          * group moves it. Putting both on one element silently drops the
          * placement -- a CSS transform overrides the presentation attribute
          * outright, which is what detached the ship from its own line.
          */}
        <g transform={`translate(${craftX} ${CRAFT_Y}) scale(${1.95 * inward} 1.95)`}>
          {/*
            * Heading is its own group. The ship leaves the way it came, so
            * without a turn it would reverse out under forward thrust -- the
            * flame pointing the way it is travelling.
            */}
          <g className="crew-heading">
            <g className="crew-craft">
              {/* Under power on the way in and the way out. */}
              <g className="crew-thrust">
                <path className="crew-thrust-flame" d="M -13 0 L -30 -3.4 L -24 0 L -30 3.4 Z" />
                <path className="crew-thrust-core" d="M -13 0 L -22 -1.8 L -19 0 L -22 1.8 Z" />
              </g>

              {craft.parts.map((part, i) => (
                <path
                key={i}
                className={`${part.line ? 'crew-line' : ''}${part.alt ? ' crew-alt' : ''}`.trim() || undefined}
                d={part.d}
              />
              ))}
              {(craft.seams ?? []).map((seam, i) => (
                <path key={`s${i}`} className="crew-seam" d={seam} />
              ))}
              {craft.badge && (
                <g
                  className="crew-livery"
                  transform={`translate(${craft.badge.x} ${craft.badge.y}) scale(${inward} 1)`}
                >
                  <HatGlyph hatId={id} size={craft.badge.size} />
                </g>
              )}
              {(craft.lights ?? []).map((light, i) => (
                <circle
                  key={`l${i}`}
                  className={`crew-blink${light.warm ? ' is-warm' : ''}`}
                  cx={light.cx}
                  cy={light.cy}
                  r={light.r}
                  style={{ animationDelay: `${light.delay}s` }}
                />
              ))}
            </g>
          </g>
        </g>

        {/* The rig: worker, torch and arc, travelling as one body. */}
        <g transform={`translate(${workX} ${WORK_Y})`}>
          <g className="crew-rig">
            <g className="crew-worker" transform="scale(0.74)">
              {form.parts.map((part, i) => (
                <path key={i} className={part.line ? 'crew-line' : undefined} d={part.d} />
              ))}
              {(form.mark ?? []).map((detail, i) => (
                <path
                  key={`m${i}`}
                  className={`crew-mark${detail.line ? ' is-line' : ''}`}
                  d={detail.d}
                  style={{ '--cz-mark-ink': form.markInk ?? `var(--tmc-hat-${id})` }}
                />
              ))}
            </g>

            <g transform={`scale(${inward} 1)`}>
              <path className="crew-torch" d="M 3 -15 L 13 -13" />
              <path className="crew-torch-tip" d="M 12.5 -15.5 L 18 -13.6 L 12.5 -11.5 Z" />

              <g className="crew-sparks">
                <circle className="crew-weld-halo" cx={19} cy={-13.5} r={7} />
                <circle className="crew-weld" cx={19} cy={-13.5} r={2.6} />
                {SPARKS.map((spark, i) => (
                  <path
                    key={i}
                    className="crew-spark"
                    d={`M 19 -13.5 l ${spark.len} 0`}
                    style={{
                      '--sx': `${spark.dx}px`,
                      '--sy': `${spark.dy}px`,
                      animationDelay: `${spark.delay}s`,
                    }}
                  />
                ))}
              </g>
            </g>
          </g>
        </g>
      </svg>
    </div>
  );
};

const MarginCrew = ({ post }) => {
  const config = CREW_POSTS[post];
  const active = Boolean(config) && hasRoom() && !calm();
  const { level, crew } = useRepairCycle(active);
  const [spot, setSpot] = useState(pickSpot);

  /*
   * A new fault opens somewhere else once the last job is finished and the
   * ship has gone. Re-rolling at any other point would move damage that a crew
   * is currently welding.
   */
  useEffect(() => {
    if (crew === 'away' && level === 0) setSpot(pickSpot());
  }, [crew, level]);

  if (!config) return null;

  return (
    <>
      <span className={`crew-fault is-${config.side}`} style={{ top: `${spot}%` }} aria-hidden="true">
        <PanelDamage level={level} side={config.side} />
      </span>
      <div
        className={`crew is-${config.side} crew-${crew}`}
        style={{ top: `calc(${spot}% + ${ALIGN}px)` }}
        aria-hidden="true"
        data-level={level}
        data-critical={level === CRITICAL ? 'true' : 'false'}
      >
        <Unit id={config.night} side={config.side} sky="night" crew={crew} />
      </div>
      <SurveyCrew id={config.day} />
    </>
  );
};

export default MarginCrew;
